import {
  useEffect,
  useId,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { Icon } from "../Icon/Icon";
import styles from "./InfoTooltip.module.css";

export type InfoTooltipLayout = "floating" | "inline";

export interface InfoTooltipProps {
  label: string;
  /** "inline" puts the panel in flow and needs a wrapping flex row as parent. */
  layout?: InfoTooltipLayout;
  children: ReactNode;
}

export function InfoTooltip({
  label,
  layout = "floating",
  children,
}: InfoTooltipProps): ReactElement {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const pointerType = useRef("");
  const id = useId();

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: PointerEvent) {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <span
      ref={wrap}
      className={layout === "inline" ? styles.inlineWrap : styles.wrap}
      // Touch fires an emulated mouseenter; the click would then close it.
      onPointerEnter={(event) => {
        if (event.pointerType === "mouse") setOpen(true);
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === "mouse") setOpen(false);
      }}
    >
      <button
        type="button"
        className={styles.trigger}
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        onPointerDown={(event) => {
          pointerType.current = event.pointerType;
        }}
        onClick={(event) => {
          const keyboard = event.detail === 0;
          if (!keyboard && pointerType.current === "mouse") return;
          setOpen((state) => !state);
        }}
        // A pointer click also focuses; only keyboard focus should open.
        onFocus={(event) => {
          if (event.currentTarget.matches(":focus-visible")) setOpen(true);
        }}
        onBlur={() => setOpen(false)}
      >
        <Icon name="help" size={15} />
      </button>
      {open ? (
        <span
          role="tooltip"
          id={id}
          className={layout === "inline" ? styles.panel : styles.bubble}
        >
          {children}
        </span>
      ) : null}
    </span>
  );
}
